import { useCallback, useState } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import { getText } from 'i18n/intl';
import {
  availabilityCollectionTimeslotsConfirmationDataSelector,
  availabilityCollectionTimeslotsSelectedTimeslotSelector,
  createAvailabilityCollectionTimeslot,
  deleteAvailabilityCollectionTimeslot,
  deleteUnsaveTimeslot,
  resetTimeslotChanges,
  setAvailabilityCollectionTimeslotUpdateRelated,
  setSelectedTimeslot,
  updateAvailabilityCollectionTimeslot,
} from 'slices/availabilityCollectionTimeslot.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { TEDrawer } from '@timeedit/ui-components';
import {
  Checkbox,
  type CheckboxChangeEvent,
  Modal,
} from '@timeedit/ui-components/antd';
import { LabelWrapper } from '@timeedit/ui-components/components/InputComponent/Input';
import TimeslotDetail from './TimeslotDetail';

type TimeslotDrawerProps = {};

type TConfirmationMode = 'closed' | 'delete' | 'update';

const confirmationLabels: Record<
  TConfirmationMode,
  { label: string; tooltip: string; okButton: string; title: string }
> = {
  closed: {
    title: '',
    label: '',
    tooltip: '',
    okButton: '',
  },
  update: {
    title: 'Confirm update of timeslot',
    label: 'Update related periods',
    tooltip:
      'If checked, will update existing periods where no invigilators have been requested to submit their availability',
    okButton: 'Save',
  },
  delete: {
    title: 'Confirm deletion of timeslot',
    label: 'Delete in related periods',
    tooltip:
      'If checked, will remove this timeslot from existing periods where no invigilators have been requested to submit their availability',
    okButton: 'Delete',
  },
};

const TimeslotDrawer = (props: TimeslotDrawerProps) => {
  const dispatch = useAppDispatch();
  const selectedTimeslot = useAppSelector(
    availabilityCollectionTimeslotsSelectedTimeslotSelector
  );
  const confirmationData = useAppSelector(
    availabilityCollectionTimeslotsConfirmationDataSelector
  );

  const [confirmationMode, setConfirmationMode] =
    useState<TConfirmationMode>('closed');

  const onClose = () => dispatch(setSelectedTimeslot(null));

  const onUpdateRelatedChange = useCallback(
    (e: CheckboxChangeEvent) => {
      dispatch(
        setAvailabilityCollectionTimeslotUpdateRelated(e.target.checked)
      );
    },
    [dispatch]
  );

  const onSave = useCallback(() => {
    if (selectedTimeslot?._id !== 'new') {
      dispatch(updateAvailabilityCollectionTimeslot());
    } else {
      dispatch(createAvailabilityCollectionTimeslot());
    }
  }, [dispatch, selectedTimeslot]);

  const onDelete = useCallback(() => {
    if (!selectedTimeslot) return;
    if (selectedTimeslot._id === 'new') {
      dispatch(deleteUnsaveTimeslot());
      return;
    }
    dispatch(deleteAvailabilityCollectionTimeslot());
  }, [dispatch, selectedTimeslot]);

  const onDiscard = useCallback(() => {
    if (!selectedTimeslot) return;
    dispatch(resetTimeslotChanges());
  }, [dispatch, selectedTimeslot]);

  const onConfirmationOk = useCallback(() => {
    if (confirmationMode === 'update') {
      onSave();
    } else if (confirmationMode === 'delete') {
      onDelete();
    }
    setConfirmationMode('closed');
  }, [confirmationMode]);

  const onConfirmationCancel = useCallback(() => {
    setConfirmationMode('closed');
  }, []);

  const handleSave = useCallback(() => {
    if (selectedTimeslot?._id !== 'new') {
      setConfirmationMode('update');
    } else {
      onSave();
    }
  }, [selectedTimeslot]);

  const handleDelete = useCallback(() => {
    setConfirmationMode('delete');
  }, []);

  return (
    <>
      <TEDrawer
        expandWidth="50vw"
        firstDrawer={{
          title:
            (selectedTimeslot?.name ?? '') === ''
              ? 'New Timeslot'
              : selectedTimeslot?.name,
          content: <TimeslotDetail />,
          drawerOpen: !!selectedTimeslot,
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText(
                'availability_collection.timeslot.delete'
              )}
              isDeleteDisable={selectedTimeslot?._id === 'new'}
              isDeletePopupDisable
              handleDelete={handleDelete}
              handleDiscard={onDiscard}
              handleSave={handleSave}
              isSaveDisable={false}
            />
          ),
          setDrawerOpen: (open) => !open && onClose(),
        }}
      />
      <Modal
        open={confirmationMode !== 'closed'}
        onOk={onConfirmationOk}
        okText={confirmationLabels[confirmationMode].okButton}
        onCancel={onConfirmationCancel}
        title={confirmationLabels[confirmationMode].title}
      >
        <LabelWrapper
          label={confirmationLabels[confirmationMode].label}
          tooltip={confirmationLabels[confirmationMode].tooltip}
        >
          <Checkbox
            checked={confirmationData.updateRelatedPeriods ?? false}
            onChange={onUpdateRelatedChange}
          />
        </LabelWrapper>
      </Modal>
    </>
  );
};

export default TimeslotDrawer;
